import {Injectable} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {FirmanteFirma} from '@/shared/ui/molecules/pdf-firma-placement/pdf-firma-placement.component';
import {ConfirmService} from '@/shared/ui/alerts/confirm-dialog.component';
import {FirmaDigitalDocumento} from '../crm/expedientes/expedientes/detail/firma-digital-documents.mock';
import {FirmaDigitalDocumentoGlobal, MOCK_FIRMA_DIGITAL_GLOBAL} from './firma-digital-global.mock';

const FIRMANTE_COLORS = [
  {color: '#2563eb', backgroundColor: 'rgba(37, 99, 235, 0.14)'},
  {color: '#059669', backgroundColor: 'rgba(5, 150, 105, 0.14)'},
  {color: '#7c3aed', backgroundColor: 'rgba(124, 58, 237, 0.14)'},
  {color: '#d97706', backgroundColor: 'rgba(217, 119, 6, 0.14)'},
];

const VENTA_FIRMANTE_ROLES = ['Cliente', 'Copropietario', 'Representante legal'] as const;

const DEFAULT_VENTA_FIRMANTE_NOMBRES: Record<(typeof VENTA_FIRMANTE_ROLES)[number], string> = {
  Cliente: 'Ana Sofía Ramírez Torres',
  Copropietario: 'Mariel Karely Cázares Navarro',
  'Representante legal': 'Carlos Alberto Méndez Ruiz',
};

@Injectable()
export class FirmaDigitalGlobalStore {
  documents: FirmaDigitalDocumentoGlobal[] = [...MOCK_FIRMA_DIGITAL_GLOBAL];
  selectedDocument: FirmaDigitalDocumentoGlobal | null = null;

  readonly firmaDigitalForm = new FormGroup({
    nombre: new FormControl('', Validators.required),
  });

  firmantesFirmaDigital: FirmanteFirma[] = [];

  private firmaPdfFile: File | null = null;

  constructor(private confirmSvc: ConfirmService) {}

  selectDocument(id: string): FirmaDigitalDocumentoGlobal | null {
    const documento = this.documents.find(item => item.id === id) ?? null;
    this.selectedDocument = documento;
    if (documento) {
      this.firmaDigitalForm.controls.nombre.setValue(documento.nombre);
      this.syncFirmantesFromDocument(documento);
    }
    return documento;
  }

  addDocument(documento: FirmaDigitalDocumentoGlobal): void {
    this.documents = [documento, ...this.documents];
  }

  setFirmantesFirmaDigital(firmantes: FirmanteFirma[]): void {
    this.firmantesFirmaDigital = firmantes;
  }

  isFirmaNombreInvalid(): boolean {
    const control = this.firmaDigitalForm.get('nombre');
    return !!(control && control.invalid && control.touched);
  }

  onFirmaPdfChange(file: File | null): void {
    this.firmaPdfFile = file;
  }

  onSolicitarFirma(): void {
    this.firmaDigitalForm.markAllAsTouched();
    // TODO: enviar solicitud de firma digital
  }

  async onCancelarFirmaDigital(): Promise<boolean> {
    const document = this.selectedDocument;
    if (!document || document.estatusGlobal !== 'Pendiente') return false;

    const ok = await this.confirmSvc.confirm('¿Seguro que deseas cancelar esta solicitud de firma?');
    if (!ok) return false;

    const updated: FirmaDigitalDocumentoGlobal = {...document, estatusGlobal: 'Cancelado'};
    this.selectedDocument = updated;
    this.documents = this.documents.map(item =>
      item.id === document.id ? updated : item,
    );
    return true;
  }

  private syncFirmantesFromDocument(documento: FirmaDigitalDocumentoGlobal): void {
    if (documento.tipo === 'Venta') {
      this.firmantesFirmaDigital = this.buildVentaFirmantes(documento);
      return;
    }

    this.firmantesFirmaDigital = [];
  }

  addFirmanteFromRepresentanteLegal(representante: {
    id: number;
    nombres: string;
    primerApellido: string;
    segundoApellido: string;
    correoElectronico: string;
    celular: string;
  }): boolean {
    const documento = this.selectedDocument;
    if (!documento || documento.tipo !== 'General') return false;

    const nombreCompleto = `${representante.nombres} ${representante.primerApellido} ${representante.segundoApellido}`.trim();
    if (this.isRepresentanteLegalAgregado(representante.id, nombreCompleto)) return false;

    const index = this.firmantesFirmaDigital.length;
    const palette = FIRMANTE_COLORS[index % FIRMANTE_COLORS.length];

    this.firmantesFirmaDigital = [
      ...this.firmantesFirmaDigital,
      {
        id: `${documento.id}-rep-${representante.id}-${Date.now()}`,
        nombre: nombreCompleto,
        rol: 'Representante legal',
        color: palette.color,
        backgroundColor: palette.backgroundColor,
        correoElectronico: representante.correoElectronico,
        celular: representante.celular,
        manual: true,
        editable: false,
      },
    ];
    return true;
  }

  isRepresentanteLegalAgregado(representanteId: number, nombreCompleto?: string): boolean {
    const marker = `-rep-${representanteId}-`;
    if (this.firmantesFirmaDigital.some(firmante => firmante.id.includes(marker))) {
      return true;
    }

    if (!nombreCompleto) return false;
    return this.firmantesFirmaDigital.some(
      firmante => firmante.rol === 'Representante legal' && firmante.nombre === nombreCompleto,
    );
  }

  private buildVentaFirmantes(documento: FirmaDigitalDocumentoGlobal): FirmanteFirma[] {
    return VENTA_FIRMANTE_ROLES.map((rol, index) => {
      const fromDocument = documento.firmantes.find(firmante => firmante.rol === rol);
      const nombre =
        fromDocument?.nombre ??
        (rol === 'Cliente' ? documento.solicitante : DEFAULT_VENTA_FIRMANTE_NOMBRES[rol]);
      const palette = FIRMANTE_COLORS[index % FIRMANTE_COLORS.length];

      return {
        id: `${documento.id}-firmante-${index}`,
        nombre,
        rol,
        color: palette.color,
        backgroundColor: palette.backgroundColor,
      };
    });
  }
}
